import type { JobData } from "~/types/job";

/**
 * JobRepository handles API interactions related to job data.
 */
export class JobRepository {
	private baseUrl = globalConfig.API + "/jobs";

	private findJobInPayloadBySlug(idOrSlug: number | string, payloadData: Record<string, unknown>): JobData | null {
		if (typeof idOrSlug !== "string") {
			return null;
		}

		for (const item of Object.values(payloadData)) {
			if (!item || typeof item !== "object") {
				continue;
			}

			const maybeJob = item as Partial<JobData>;
			if (maybeJob.slug && typeof maybeJob.slug === "object" && Object.values(maybeJob.slug).includes(idOrSlug)) {
				return maybeJob as JobData;
			}
		}

		return null;
	}

	/**
	 * Finds the cache key from the Nuxt payload data by matching the slug.
	 *
	 * This function searches through the cached payload data to find a job that matches
	 * the provided slug. It first checks the `slugs` object (Record<string, string>),
	 * then falls back to checking the `slug` property directly.
	 *
	 * If a matching job is found in the cache, it returns the cache key identifier
	 * that was used to store it. Otherwise, it constructs a new key using the job ID.
	 *
	 * @param idOrSlug - The job identifier (either ID as number or slug as string)
	 * @param payloadData - The Nuxt app payload data containing cached job information
	 * @returns The cache key (either the existing cache key or `job-${id}` format)
	 */
	private findJobIdFromSlug(idOrSlug: number | string, payloadData: Record<string, unknown>, locale: string): string {
		// If it's already a number (ID), return it directly
		if (typeof idOrSlug !== "string") {
			return `job-${idOrSlug}`;
		}

		// Search through payload data for a matching slug and get its cache key
		const cacheEntry = Object.entries(payloadData).find(([_key, item]: [string, unknown]) => {
			if (!item || typeof item !== "object") {
				return false;
			}

			const maybeJob = item as Partial<JobData>;
			if (maybeJob.slug && typeof maybeJob.slug === "object") {
				if (Object.values(maybeJob.slug).includes(idOrSlug)) {
					return true;
				}
			}
			return false;
		});

		// If found in cache, return the cache key identifier
		if (cacheEntry) {
			return cacheEntry[0];
		}

		// If not found in cache, construct a key with the job ID or slug
		return `job-${idOrSlug}-${locale}`;
	}

	async getJob(
		idOrSlug: number | string,
		locale: string
	): Promise<{ data: Pick<JobData, string> | null; error: unknown }> {
		const payloadData = useNuxtApp().payload.data;
		const cachedJob = this.findJobInPayloadBySlug(idOrSlug, payloadData);
		const requestIdOrSlug = cachedJob?.id ?? idOrSlug;

		// Get the cache key by searching for slug in payload data
		const cacheKey = this.findJobIdFromSlug(idOrSlug, payloadData, locale);

		const { data, error } = await useFetch<JobData>(`${this.baseUrl}/${requestIdOrSlug}`, {
			method: "GET",
			key: cacheKey,
			query: {
				locale: locale,
			},
		});

		if (error.value) {
			console.error("JobRepository.getJob error:", error.value?.status);
		}

		return { data: data?.value, error: error.value };
	}
}
